import { useEffect, useState } from "react";
import { Tag, Calendar, CalendarClock, Eye, FileText, Pencil, Save, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const formatPrice = (price) => {
  const num = typeof price === "string" ? parseFloat(price.replace(/[$,]/g, "")) : price;
  if (isNaN(num) || num == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(num);
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

// Convert an ISO timestamp to a YYYY-MM-DD string in local time, for <input type="date">.
const isoToDateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const daysOnMarket = (listingDate) => {
  if (!listingDate) return "—";
  const start = new Date(listingDate);
  if (isNaN(start.getTime())) return "—";
  const diff = Math.floor((Date.now() - start.getTime()) / (1000 * 60 * 60 * 24));
  return diff < 0 ? 0 : diff;
};

const CardShell = ({ icon: Icon, label, children, fullWidthOnMobile = false, testid }) => (
  <div
    className={`rounded-md border border-slate-100 bg-slate-50/60 px-4 py-3.5 ${
      fullWidthOnMobile ? "col-span-2 sm:col-span-1" : ""
    }`}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-slate-400">
      <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
      {label}
    </div>
    {children}
  </div>
);

export const ListingActivityPanel = ({ transaction, transactionId, isAgent = false, onUpdate }) => {
  const [offerCount, setOfferCount] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchOfferCount = async () => {
      try {
        const { count, error } = await supabase
          .from("offers")
          .select("*", { count: "exact", head: true })
          .eq("transaction_id", transactionId);
        if (cancelled) return;
        if (error) {
          setOfferCount(0);
        } else {
          setOfferCount(count || 0);
        }
      } catch {
        if (!cancelled) setOfferCount(0);
      }
    };
    if (transactionId) fetchOfferCount();
    return () => { cancelled = true; };
  }, [transactionId]);

  const listingDate = transaction?.listing_date || null;
  const showings = transaction?.showings_count;

  const openEdit = () => {
    setDraft(isoToDateInput(listingDate));
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft("");
  };

  const handleSave = async () => {
    if (!draft) {
      toast.error("Please choose a date");
      return;
    }
    setSaving(true);
    try {
      // Store as midday UTC for the chosen calendar date — keeps Days on Market math stable across timezones.
      const iso = new Date(`${draft}T12:00:00Z`).toISOString();
      const { error } = await supabase
        .from("transactions")
        .update({ listing_date: iso })
        .eq("id", transactionId);
      if (error) throw error;
      onUpdate?.({ listing_date: iso });
      setEditing(false);
      toast.success("Listing date updated");
    } catch (err) {
      console.error("[ListingActivity] Save listing_date error:", err);
      toast.error("Failed to update listing date");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-100 shadow-soft p-6 my-6" data-testid="listing-activity-panel">
      <div className="flex items-baseline justify-between mb-5">
        <h3 className="font-heading text-lg font-semibold text-slate-900">Listing Activity</h3>
        <span className="text-[11px] font-semibold tracking-widest uppercase text-slate-400">Offer &amp; Negotiation</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <CardShell icon={Tag} label="List Price" testid="listing-activity-list-price">
          <p className="mt-1.5 text-base font-semibold text-slate-900 tabular-nums">{formatPrice(transaction?.price)}</p>
        </CardShell>

        <CardShell icon={Calendar} label="Listing Date" testid="listing-activity-listing-date">
          {editing ? (
            <div className="mt-1.5 space-y-2">
              <Input
                type="date"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="h-8 text-sm"
                data-testid="listing-date-input"
              />
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="listing-date-save-btn"
                >
                  <Save className="w-3 h-3 mr-1" />
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={cancelEdit}
                  disabled={saving}
                  data-testid="listing-date-cancel-btn"
                >
                  <X className="w-3 h-3 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <p className="text-base font-semibold text-slate-900 tabular-nums">{formatDate(listingDate)}</p>
              {isAgent && (
                <button
                  type="button"
                  onClick={openEdit}
                  className="text-slate-400 hover:text-slate-700 transition-colors p-0.5 rounded"
                  title="Edit listing date"
                  data-testid="listing-date-edit-btn"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </CardShell>

        <CardShell icon={CalendarClock} label="Days on Market" testid="listing-activity-days-on-market">
          <p className="mt-1.5 text-base font-semibold text-slate-900 tabular-nums">{daysOnMarket(listingDate)}</p>
        </CardShell>

        <CardShell icon={Eye} label="Showings" testid="listing-activity-showings">
          <p className="mt-1.5 text-base font-semibold text-slate-900 tabular-nums">{showings == null ? "—" : showings}</p>
        </CardShell>

        <CardShell icon={FileText} label="Offers" fullWidthOnMobile testid="listing-activity-offers">
          <p className="mt-1.5 text-base font-semibold text-slate-900 tabular-nums">{offerCount == null ? "—" : offerCount}</p>
        </CardShell>
      </div>
    </div>
  );
};
